import { useEffect, useState, useCallback } from 'react';
import { AuthContext } from './authContextObject';
import apiClient from '../api/client';
import i18n from '../i18n';
import { applyServerTheme, setTheme } from '../theme';
import { peekPending, clearPending } from '../pendingPreferences';



export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    login: () => {},
    logout: () => {},
  });

  const backendBaseUrl = import.meta.env.VITE_API_URL.replace('/api/v1', '');

  const checkAuth = useCallback(async () => {
    try {
      const resp = await apiClient.get('/me');
      // Load language preference; fallback gracefully if endpoint unavailable (local dev).
      try {
        const prefsResp = await apiClient.get('/me/preferences');

        // A change made while signed out is newer than the account's value, so
        // it is promoted rather than overwritten (design D4). Anything else in
        // localStorage is only a mirror of a previous login and must not be
        // written back, or the last device used would silently clobber the
        // account setting.
        const pending = peekPending();
        if (pending && (pending.theme || pending.language)) {
          try {
            const promoted = await apiClient.put('/me/preferences', pending);
            clearPending();
            if (promoted.data?.language) i18n.changeLanguage(promoted.data.language);
            if (promoted.data?.theme) setTheme(promoted.data.theme);
          } catch (err) {
            // Left pending deliberately: the choice still applies locally and
            // gets another chance at the next sign-in. Reported rather than
            // swallowed.
            const status = err?.response?.status;
            console.warn(
              `[AuthContext] could not promote the signed-out preference${status ? ` (HTTP ${status})` : ''}; it applies locally and will retry at next sign-in`,
              err,
            );
          }
        } else {
          const lang = prefsResp.data?.language;
          if (lang === 'en' || lang === 'fr') {
            i18n.changeLanguage(lang);
          }
          // Server wins over whatever this browser remembered pre-paint, so the
          // same user does not see two appearances across devices. Applied after
          // first paint by necessity — the pre-paint script in index.html is what
          // stops that being a visible flash.
          applyServerTheme(prefsResp.data?.theme);
        }
      } catch { /* keep localStorage/navigator default */ }
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: resp.data,
        login: () => window.location.href = `${backendBaseUrl}/auth/login`,
        logout: () => window.location.href = `${backendBaseUrl}/auth/logout`,
      });
    } catch {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        login: () => window.location.href = `${backendBaseUrl}/auth/login`,
        logout: () => window.location.href = `${backendBaseUrl}/auth/logout`,
      });
    }
  }, [backendBaseUrl]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);


  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
};
